import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import { MdOutlineLegendToggle } from "react-icons/md";
import Swal from "sweetalert2";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";
import { FaRegCircleUser } from "react-icons/fa6";
import { IoMdLogOut } from "react-icons/io";
import axios from "axios";
import apiConfig from "./credentials/apiConfig";
import './custom.scss';
import { useAuth } from '../AuthProvider';

const pageTitleMap = {
  '/engagements': 'Engagements',
  '/settings': 'Settings',
  '/client': 'Clients',
  '/help-support': 'Help & Support',
  '/profile': 'Profile',
  '/edit-profile': 'Edit Profile',
  '/engagements/edit-engagement-setup': 'Engagements',
  '/documentsummary': 'Document Summary',
  '/investment-schedules/create-new-analysis': 'Investment Schedule Analysis Setup',
  '/investment-schedules/details': 'Investment Schedule Report',
  '/investment-schedules': 'Investment Schedules',
};

const Header = ({ toggleSidebar, sidebarToggled, onProfileLoaded }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;
  // const pageName = pageTitleMap[location.pathname] || 'Dashboard';
  const pageName = Object.entries(pageTitleMap).find(([path]) =>
    currentPath.startsWith(path)
  )?.[1] || 'Dashboard';
  const navigate = useNavigate();
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    profileImageUrl: null,
    email: '',
    title: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [profileInitials, setProfileInitials] = useState("GU");
  const [imageError, setImageError] = useState(false);

  // Function to generate profile initials
  const generateInitials = (firstName, lastName, email) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    } else if (email) {
      return email.charAt(0).toUpperCase();
    }
    return "GU"; // Guest User
  };

  // Fetch user data when component mounts or when auth state changes
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        // Get stored user data
        const userId = localStorage.getItem("userId");
        const authToken = localStorage.getItem("authToken");
        const email = localStorage.getItem("userEmail");

        // Prepare initial initials (fallback)
        const initials = generateInitials('', '', email);
        setProfileInitials(initials);

        // Check if we have userId and authToken to fetch profile
        if (userId) {
          try {
            // Fetch user profile data from API
            const response = await axios.get(
              `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
              {
                headers: {
                  "x-api-key": apiConfig["x-api-key"],
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${authToken}`
                }
              }
            );

            const data = response.data;
            if (data && data.success && data.user) {
              // Update user state with data from API
              const userData = {
                firstName: data.user.firstName || '',
                lastName: data.user.lastName || '',
                companyName: data.user.companyName || '',
                profileImageUrl: data.user.profileImageUrl || null,
                email: data.user.email || email || '',
                title: data.user.title || ''
              };

              setUser(userData);

              // Generate initials from the actual user data
              const userInitials = generateInitials(
                userData.firstName,
                userData.lastName,
                userData.email
              );
              setProfileInitials(userInitials);

              // If we didn't have email before, store it now
              if (!localStorage.getItem("userEmail") && data.user.email) {
                localStorage.setItem("userEmail", data.user.email);
              }
            }
          } catch (error) {
            console.error("Error fetching user profile from API:", error);
          }
        }

        // Set profile loaded to true in parent component
        if (onProfileLoaded) {
          onProfileLoaded(true);
        }
      } catch (error) {
        console.error("Error in fetchUserData:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Add event listener for storage changes
    const handleStorageChange = (event) => {
      fetchUserData();
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom auth events
    window.addEventListener('auth-updated', handleStorageChange);

    // Initial fetch
    fetchUserData();

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-updated', handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#073E72",
      cancelButtonColor: "#fff",
      confirmButtonText: "Yes, Logout!",
      customClass: {
        popup: "custom-swal-popup",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Logging out...",
          icon: "info",
          showConfirmButton: false,
        });
        logout();
        setTimeout(() => {
          Swal.close();
          navigate("/");
          window.location.reload();
        }, 2000);
      }
    });
  };

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
  };

  // Display full name if available, otherwise show email or "Guest User"
  const fullName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.email ? user.email : "Guest User";


  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };


  return (
    <Navbar className="bg-white py-3 box-shadow header">
      <Container fluid>
        {/* Sidebar toggle button */}
        <div
          className="sidebar-toggle"
          onClick={() => toggleSidebar()}
          style={{ cursor: "pointer" }}
        >
          <MdOutlineLegendToggle size={24} className="text-light" />
        </div>
        {pageName === 'Investment Schedules' ? (
          <div className="ms-3 my-auto">
            <h3 className="text-dark fw-bold mb-0 header-page-names" style={{ fontSize: '20px' }}>{pageName}</h3>
            <p className="text-muted mb-0" style={{ fontSize: '13px', lineHeight: '1.3' }}>
              Venture capital and private equity fund analysis.
            </p>
          </div>
        ) : pageName === 'Investment Schedule Analysis Setup' ? (
          <div className="ms-3 my-auto">
            <h3 className="text-dark fw-bold mb-0 header-page-names" style={{ fontSize: '20px' }}>{pageName}</h3>
            <p className="text-muted mb-0" style={{ fontSize: '13px', lineHeight: '1.3' }}>
              Upload investment schedules and financial documents to generate comprehensive analysis reports with year-over-year comparisons and real-time market intelligence.
            </p>
          </div>
        ) : (
          <h3 className="text-dark fw-bold ms-3 my-auto header-page-names" style={{ fontSize: '20px' }}>{pageName}</h3>
        )}
        <Navbar.Collapse className="justify-content-end mx-3">
          <Dropdown align="end" className="settings-block-ucr">
            <Dropdown.Toggle variant="link" id="dropdown-basic" className="d-flex text-decoration-none align-items-center">
              {isLoading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : user.profileImageUrl && !imageError ? (
                <div className="profile-image-container" style={{ width: "50px", height: "50px", marginRight: "8px", overflow: "hidden" }}>
                  <img
                    src={user.profileImageUrl}
                    alt="profile"
                    className="img-fluid rounded-circle w-100 h-100 object-fit-cover"
                    style={{ objectFit: "cover" }}
                    onError={handleImageError}
                  />
                </div>
              ) : (
                <div className="profile-initials rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                  style={{ width: "50px", height: "50px", marginRight: "8px" }}>
                  {profileInitials}
                </div>
              )}
              {/* <div style={{ textAlign: 'left', lineHeight: 'normal' }}>
                <p className="text-dark m-0 text-left" >{fullName}</p>
                <span className="text-light">{user.companyName || "Company"}</span>
                {user.title && <small className="d-block text-secondary">{user.title}</small>}
              </div> */}
              <div style={{ textAlign: 'left', lineHeight: 'normal' }}>
                <p
                  className="text-dark m-0 text-left"
                  title={fullName}
                >
                  {truncateText(fullName)}
                </p>
                <span
                  className="text-light"
                  title={user.companyName || "Company"}
                >
                  {truncateText(user.companyName || "Company")}
                </span>
                {user.title && (
                  <small className="d-block text-secondary">{user.title}</small>
                )}
              </div>

            </Dropdown.Toggle>
            <Dropdown.Menu className="border-0 shadow mt-3 py-3">
              <Dropdown.Item className="px-4 mb-2">
                <Link to="/profile" className="text-decoration-none text-light fw-bold">
                  <FaRegCircleUser color="#677289" size={18} className="me-3" />View Profile
                </Link>
              </Dropdown.Item>
              <Dropdown.Item onClick={handleLogout} className="text-light px-4 fw-bold ">
                <IoMdLogOut color="#677289" size={20} className="me-3" />Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;